import apiClient from './apiClient'
import axios, { CancelTokenSource } from 'axios'
import { shouldForceError } from '../utils/devErrors'

// Which company's assistant a chat talks to (mirrors the backend Company enum).
export type Company = 'gails' | 'tbf'

export const COMPANY_LABELS: Record<Company, string> = {
  gails: "Gail's",
  tbf: 'The Bread Factory',
}

// Define MessageData interface locally to avoid circular dependencies
export interface MessageData {
  id: string
  role: 'user' | 'assistant' | 'error'
  content: string
  timestamp: number
  documents?: DocumentReference[]
  daxQueries?: DaxQuery[]
  streamingComplete?: boolean
  feedback?: FeedbackType | null
}

interface QueryInput {
  messages: MessageData[]
  user_id: string
  chat_id: string
}

interface QueryOutput {
  message: string
}

interface DocumentReference {
  filename: string
  file_id: string
  relevant_pages?: [number, number]
  web_url?: string
}

export interface DaxQuery {
  title: string
  dax: string
  model_name?: string | null
  // Results are captured/stored but not displayed in the UI.
  rows?: Record<string, unknown>[] | null
  row_count?: number | null
  error?: string | null
}

export enum FeedbackType {
  POSITIVE = 'positive',
  NEGATIVE = 'negative',
}

export interface MessageFeedbackData {
  type: FeedbackType
  reason?: string | null
}

interface StreamChunk {
  type:
    | 'response_text'
    | 'relevant_documents'
    | 'dax_queries'
    | 'error'
    | 'message_id'
  content: string | DocumentReference[] | DaxQuery[] | object
}

export const getChatResponse = async (
  messages: MessageData[],
  userId: string,
  chatId: string,
  onStreamUpdate: (chunk: string) => void,
  onDocumentsReceived: (documents: DocumentReference[]) => void,
  onNewResponseId: (responseId: string) => void,
  cancelToken: CancelTokenSource,
  onDaxQueriesReceived?: (daxQueries: DaxQuery[]) => void,
  company?: Company,
  onError?: (message: string) => void
): Promise<string> => {
  // If we have a streaming handler, use fetch for streaming
  if (onStreamUpdate) {
    try {
      // Dev-only: surface a forced failure through the same error path as a real one.
      if (shouldForceError(`/chats/${chatId}/messages`)) {
        onError?.('Forced error — this is what a failure looks like.')
        throw new Error('Forced error (dev)')
      }
      // Get the base URL from apiClient
      const baseUrl = apiClient.defaults.baseURL || ''

      // Create AbortController to handle cancellation
      const controller = new AbortController()

      // Link the axios cancelToken to the AbortController
      if (cancelToken) {
        cancelToken.cancel = () => {
          controller.abort()
          return 'Request canceled'
        }
      }

      const response = await fetch(`${baseUrl}/chats/${chatId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key':
            (apiClient.defaults.headers['X-API-Key'] as string) || '',
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        body: JSON.stringify({
          messages,
          user_id: userId,
          company: company ?? null,
        }),
        signal: controller.signal,
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      // Process the stream
      const reader = response.body?.getReader()
      if (!reader) {
        throw new Error('Unable to read response stream')
      }

      const decoder = new TextDecoder()
      let fullResponse = ''
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Process complete lines from the buffer
        const lines = buffer.split('\n')
        buffer = lines.pop() || '' // Keep the incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonData = line.substring(6) // Remove 'data: ' prefix
              if (jsonData.trim() === '') continue // Skip empty data lines

              const chunk: StreamChunk = JSON.parse(jsonData)
              console.log('chunk', chunk)
              if (
                chunk.type === 'response_text' &&
                typeof chunk.content === 'string'
              ) {
                fullResponse += chunk.content
                onStreamUpdate(chunk.content)
              } else if (
                chunk.type === 'relevant_documents' &&
                Array.isArray(chunk.content)
              ) {
                onDocumentsReceived(chunk.content as DocumentReference[])
              } else if (
                chunk.type === 'dax_queries' &&
                Array.isArray(chunk.content)
              ) {
                onDaxQueriesReceived?.(chunk.content as DaxQuery[])
              } else if (chunk.type === 'error') {
                console.error('Stream error:', chunk.content)
                const detail =
                  chunk.content &&
                  typeof chunk.content === 'object' &&
                  'error' in chunk.content
                    ? String((chunk.content as { error: unknown }).error)
                    : ''
                onError?.(detail)
              } else if (chunk.type === 'message_id') {
                console.log('new response id', chunk.content)
                onNewResponseId(chunk.content as string)
              }
            } catch (parseError) {
              // If JSON parsing fails, treat as old format (fallback)
              console.warn(
                'Failed to parse JSON chunk, treating as plain text:',
                parseError
              )
              const plainText = line.startsWith('data: ')
                ? line.substring(6)
                : line
              if (plainText.trim()) {
                fullResponse += plainText
                onStreamUpdate(plainText)
              }
            }
          }
        }
      }

      return fullResponse
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Request canceled')
        return 'Request canceled'
      }
      console.error('Error getting chat response:', error)
      throw error
    }
  }
  // For non-streaming requests, use the original axios implementation
  else {
    try {
      const response = await apiClient.post<QueryOutput>(
        '/query',
        {
          messages,
          user_id: userId,
          chat_id: chatId,
        } as QueryInput,
        {
          cancelToken: cancelToken?.token,
        }
      )

      return response.data.message
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Request canceled:', error.message)
        return 'Request canceled'
      }
      console.error('Error getting chat response:', error)
      throw error
    }
  }
}

// Create a cancel token source
export const createCancelTokenSource = (): CancelTokenSource => {
  return axios.CancelToken.source()
}

export type { DocumentReference }

export const addMessageFeedback = async (
  chatId: string,
  messageId: string,
  feedbackData: MessageFeedbackData | null,
  cancelToken?: CancelTokenSource
): Promise<void> => {
  try {
    await apiClient.post(
      `/chats/${chatId}/messages/${messageId}/feedback`,
      feedbackData,
      {
        cancelToken: cancelToken?.token,
      }
    )
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Add feedback request canceled:', error.message)
    } else {
      console.error('Error adding message feedback:', error)
    }
    // Re-throw the error if you want the caller to handle it,
    // or handle it here as appropriate for your app's error handling strategy.
    throw error
  }
}

// Chat management interfaces
export interface ChatOverview {
  id: string
  title: string | null
  timestamp: number
}

// Backend message format (what the API returns)
interface BackendMessageData {
  id: string
  role: 'user' | 'assistant' | 'error'
  content: string
  timestamp: number
  relevant_documents?: DocumentReference[]
  dax_queries?: DaxQuery[]
  feedback?: { type: FeedbackType; reason?: string | null } | null
}

// Backend chat format
interface BackendChat {
  id: string
  title: string | null
  user_id: string
  source: string
  messages: BackendMessageData[]
  company?: Company | null
  deleted_at: number | null
}

export interface Chat {
  id: string
  title: string | null
  user_id: string
  source: string
  messages: MessageData[]
  company?: Company | null
  deleted_at: number | null
}

// Get all chats overview for the current user
export const getChatsOverview = async (
  cancelToken?: CancelTokenSource
): Promise<ChatOverview[]> => {
  try {
    const response = await apiClient.get<ChatOverview[]>('/chats/overview', {
      cancelToken: cancelToken?.token,
    })
    return response.data
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Get chats overview request canceled:', error.message)
      return []
    } else {
      console.error('Error fetching chats overview:', error)
      throw error
    }
  }
}

// Get specific chat by ID
export const getChat = async (
  chatId: string,
  cancelToken?: CancelTokenSource
): Promise<Chat> => {
  try {
    const response = await apiClient.get<BackendChat>(`/chats/${chatId}`, {
      cancelToken: cancelToken?.token,
    })

    // Transform relevant_documents to documents for frontend compatibility
    const chatData = response.data
    const transformedMessages: MessageData[] = chatData.messages.map(
      message => {
        // Transform relevant_documents to documents and preserve all other fields
        const { relevant_documents, dax_queries, feedback, ...otherFields } =
          message
        return {
          ...otherFields,
          documents: relevant_documents || undefined,
          daxQueries: dax_queries || undefined,
          feedback: feedback?.type || null,
        }
      }
    )

    return {
      ...chatData,
      messages: transformedMessages,
    }
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Get chat request canceled:', error.message)
      throw error
    } else {
      console.error('Error fetching chat:', error)
      throw error
    }
  }
}

// Delete chat (soft delete)
export const deleteChat = async (
  chatId: string,
  cancelToken?: CancelTokenSource
): Promise<void> => {
  try {
    await apiClient.delete(`/chats/${chatId}`, {
      cancelToken: cancelToken?.token,
    })
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Delete chat request canceled:', error.message)
    } else {
      console.error('Error deleting chat:', error)
      throw error
    }
  }
}

// Rename chat (this would require a new backend endpoint)
export const renameChat = async (
  chatId: string,
  newTitle: string,
  cancelToken?: CancelTokenSource
): Promise<void> => {
  try {
    await apiClient.patch(
      `/chats/${chatId}`,
      { title: newTitle },
      {
        cancelToken: cancelToken?.token,
      }
    )
  } catch (error) {
    if (axios.isCancel(error)) {
      console.log('Rename chat request canceled:', error.message)
    } else {
      console.error('Error renaming chat:', error)
      throw error
    }
  }
}
